import { observer } from "mobx-react-lite";
import { FC, useState, useCallback, useEffect } from "react";
import { Duration } from "luxon";
import styled from "@emotion/styled";
import { DebugModeTapDetector } from "@/components/layout/components/account-profile/debug-mode-tap-detector";
import { AccountInfo } from "@/components/layout/components/account-info/account-info";
import { OAuthCardWrapper } from "@/pages/oauth-consent/oauth-card-wrapper";
import { windowModule } from "@/modules/window";
import { MDSButton, MDSButtonVariant, MDSLogo, MDSLogoKind } from "@/mds";

const AUTO_REDIRECT_DELAY = Duration.fromObject({ seconds: 2 });

enum CardState {
  Initial = "initial",
  Approved = "approved",
  Denied = "denied",
  Error = "error",
}

export interface OAuthCardProps {
  appName: string;
  /**
   * Not currently used.
   * In the future, we'll show the "real" scopes from the backend.
   */
  scopeStr?: string;
  accountInfo: {
    profilePhotoUrl: string | null;
    profileDisplayName: string;
    profileEmailAddress: string;
  };
  handleApprove: () => Promise<string | undefined>;
  handleDeny: () => Promise<string | undefined>;
}

export const OAuthCard: FC<OAuthCardProps> = observer(
  ({ appName, accountInfo, handleApprove, handleDeny }) => {
    const [isApproveLoading, setIsApproveLoading] = useState(false);
    const [isDenyLoading, setIsDenyLoading] = useState(false);
    const [cardState, setCardState] = useState<CardState>(CardState.Initial);
    const [redirectUrl, setRedirectUrl] = useState<string | null>(null);

    useEffect(() => {
      if (!redirectUrl) return;
      if (cardState !== CardState.Approved && cardState !== CardState.Denied) return;

      const timeoutId = setTimeout(() => {
        void windowModule.navigateToExternal({ url: new URL(redirectUrl) });
      }, AUTO_REDIRECT_DELAY.toMillis());

      return () => {
        clearTimeout(timeoutId);
      };
    }, [cardState, redirectUrl]);

    const onClickApprove = useCallback(async () => {
      setIsApproveLoading(true);
      try {
        const nextRedirectUrl = await handleApprove();
        setRedirectUrl(nextRedirectUrl ?? null);
        setCardState(CardState.Approved);
      } catch (_unknownErr) {
        setCardState(CardState.Error);
      } finally {
        setIsApproveLoading(false);
      }
    }, [handleApprove]);

    const onClickDeny = useCallback(async () => {
      setIsDenyLoading(true);
      try {
        const nextRedirectUrl = await handleDeny();
        setRedirectUrl(nextRedirectUrl ?? null);
        setCardState(CardState.Denied);
      } catch (_unknownErr) {
        setCardState(CardState.Error);
      } finally {
        setIsDenyLoading(false);
      }
    }, [handleDeny]);

    if (cardState === CardState.Approved) {
      return (
        <OAuthCardWrapper>
          <CardSection>
            <TitleSection>Access Approved</TitleSection>
            <MessageSection>
              You have successfully granted access to {appName}. Redirecting you back now. Do not
              close this window yet.
            </MessageSection>
            {redirectUrl ? (
              <MessageSection>
                If you are not redirected automatically,{" "}
                <FallbackLink
                  href={redirectUrl}
                  onClick={event => {
                    event.preventDefault();
                    void windowModule.navigateToExternal({ url: new URL(redirectUrl) });
                  }}
                >
                  click here to continue
                </FallbackLink>
                .
              </MessageSection>
            ) : null}
          </CardSection>
        </OAuthCardWrapper>
      );
    }

    if (cardState === CardState.Denied) {
      return (
        <OAuthCardWrapper>
          <CardSection>
            <TitleSection>Canceled</TitleSection>
            <MessageSection>Request canceled. You can now close this window.</MessageSection>
            {redirectUrl ? (
              <MessageSection>
                If you want to return now,{" "}
                <FallbackLink
                  href={redirectUrl}
                  onClick={event => {
                    event.preventDefault();
                    void windowModule.navigateToExternal({ url: new URL(redirectUrl) });
                  }}
                >
                  click here
                </FallbackLink>
                .
              </MessageSection>
            ) : null}
          </CardSection>
        </OAuthCardWrapper>
      );
    }

    if (cardState === CardState.Error) {
      return (
        <OAuthCardWrapper>
          <CardSection>
            <TitleSection>Something went wrong</TitleSection>
            <MessageSection>
              An error occurred while processing your request. Please refresh the page and try
              again.
            </MessageSection>
          </CardSection>
        </OAuthCardWrapper>
      );
    }

    return (
      <OAuthCardWrapper>
        <CardSection>
          <TitleSection>{appName} wants permission to access your Mem Account</TitleSection>

          <DebugModeTapDetector>
            <StyledLogo kind={MDSLogoKind.Arrows} />
          </DebugModeTapDetector>

          <AccountSection>
            <AccountInfo
              profilePhotoUrl={accountInfo.profilePhotoUrl}
              profileDisplayName={accountInfo.profileDisplayName}
              profileEmailAddress={accountInfo.profileEmailAddress}
            />
          </AccountSection>

          <ScopeSection>
            <ScopeMessage>This will allow {appName} to:</ScopeMessage>
            <ScopeItem>• Access your notes</ScopeItem>
            <ScopeItem>• Access your collections</ScopeItem>
          </ScopeSection>

          <PolicySection>
            By clicking Allow Access, you allow this app to use your information in accordance to
            their terms of service and privacy policies. You can change this at any time in the Mem
            application.
          </PolicySection>
        </CardSection>

        <ActionSection>
          <MDSButton
            variant={MDSButtonVariant.Transparent}
            onClick={onClickDeny}
            isDisabled={isApproveLoading}
            isLoading={isDenyLoading}
          >
            Cancel
          </MDSButton>
          <MDSButton
            variant={MDSButtonVariant.Contrast}
            onClick={onClickApprove}
            isDisabled={isDenyLoading}
            isLoading={isApproveLoading}
          >
            Allow Access
          </MDSButton>
        </ActionSection>
      </OAuthCardWrapper>
    );
  }
);

const AccountSection = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-top: 8px;
`;

const TitleSection = styled.div`
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.fontSizes["header-m"]};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  letter-spacing: ${({ theme }) => theme.lineSpacings["header-l"]};
  margin-bottom: 28px;
  text-align: center;
`;

const ScopeSection = styled.div`
  display: flex;
  flex-direction: column;
  flex-wrap: nowrap;
  align-items: flex-start;
  width: 100%;
  gap: 8px;
  margin-top: 32px;
  margin-bottom: 32px;
`;

const ScopeMessage = styled.div`
  font-size: ${({ theme }) => theme.fontSizes["paragraph-s"]};
  font-weight: ${({ theme }) => theme.fontWeights.regular};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const ScopeItem = styled.div`
  font-size: ${({ theme }) => theme.fontSizes["paragraph-s"]};
  font-weight: ${({ theme }) => theme.fontWeights.regular};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const PolicySection = styled.div`
  font-size: ${({ theme }) => theme.fontSizes["text-xs"]};
  font-weight: ${({ theme }) => theme.fontWeights.regular};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const MessageSection = styled.div`
  font-size: ${({ theme }) => theme.fontSizes["paragraph-s"]};
  font-weight: ${({ theme }) => theme.fontWeights.regular};
  color: ${({ theme }) => theme.colors.text.primary};
  text-align: center;
  margin-top: 16px;
`;

const FallbackLink = styled.a`
  color: ${({ theme }) => theme.colors.text.primary};
  text-decoration: underline;
  cursor: pointer;
`;

const CardSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
`;

const ActionSection = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  margin-top: 24px;
  justify-content: space-between;
  width: 100%;
`;

const StyledLogo = styled(MDSLogo)`
  height: 56px;
  width: 56px;
`;
